/**
 * Vollständiges Laden einer Tabellen-Abfrage, seitenweise.
 *
 * **Warum es das braucht:** PostgREST kappt jede Antwort bei `max_rows` (in diesem Projekt 1000)
 * — und zwar STILL. Eine Abfrage ohne Limit sieht im Code aus, als hole sie alles, liefert aber
 * höchstens 1000 Zeilen; ein `.limit(2000)` täuscht zusätzlich eine Grenze vor, die es so gar
 * nicht gibt. Im Kundenshop mit 1783 Artikeln fehlten dadurch rund 780 Artikel in der
 * Produktübersicht, ohne jeden Hinweis in der Oberfläche.
 *
 * Betreiber-Vorgabe 27.08.2026: „Es müssten aber ALLE Artikel angezeigt werden können. Ein fixes
 * Limit wäre falsch. Wir können in batches importieren, es müssen aber alle möglich sein."
 *
 * Deshalb: keine Obergrenze mehr im Aufrufer, sondern `range()`-Seiten, bis eine unvollständige
 * Seite das Ende anzeigt. Das ist unabhängig von `max_rows` richtig und wächst mit dem Bestand.
 */

export interface PageResult<T> {
  data: T[] | null;
  error: { message: string } | null;
}

/**
 * Ruft `fetchPage` mit fortlaufenden Bereichen auf, bis eine Seite kürzer als `pageSize` ist.
 *
 * @param fetchPage bekommt `from`/`to` (beide inklusiv, wie `PostgrestFilterBuilder.range`)
 * @param pageSize  Zeilen je Abruf. Standard 1000 = die PostgREST-Obergrenze; kleiner zu wählen
 *                  bringt nichts, größer wird ohnehin gekappt.
 * @param maxPages  Notbremse gegen eine Endlosschleife, falls der Server dauerhaft volle Seiten
 *                  liefert. 200 Seiten = 200.000 Zeilen, weit jenseits jedes realen Bestands.
 *
 * Ein Fehler bricht sofort ab und wird durchgereicht; die bis dahin gelesenen Zeilen kommen mit,
 * damit der Aufrufer entscheiden kann, ob er einen Teilbestand anzeigt oder eine Meldung bringt.
 */
export async function loadAllRows<T>(
  fetchPage: (from: number, to: number) => PromiseLike<PageResult<T>>,
  pageSize = 1000,
  maxPages = 200,
): Promise<{ data: T[]; error: { message: string } | null; truncated: boolean }> {
  const all: T[] = [];
  for (let page = 0; page < maxPages; page++) {
    const from = page * pageSize;
    const res = await fetchPage(from, from + pageSize - 1);
    if (res.error) return { data: all, error: res.error, truncated: false };
    const rows = res.data ?? [];
    all.push(...rows);
    // Kürzere Seite = Ende erreicht. Genau `pageSize` heißt: es kommt vermutlich noch mehr.
    if (rows.length < pageSize) return { data: all, error: null, truncated: false };
  }
  // Notbremse gegriffen — der Aufrufer soll das sichtbar machen, nicht stillschweigend kürzen.
  return { data: all, error: null, truncated: true };
}
